import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { UserProfile } from './userProfileService';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';


export interface NewJoiner {
  id: string;
  name: string;
  email: string;
  department: string;
  position: string;
  startDate: string;
  personioStatus: string;
}

export interface NewJoinersResponse {
  joiners: NewJoiner[];
}

export interface G2KEntry {
  id: string;
  joiner: NewJoiner;
  employee: UserProfile;
  durationMinutes: 15 | 30;
}

/** Webhook body the Starbase G2K scheduler reads (one call per new joiner). */
interface G2KSchedulerPayload {
  new_joiner: { name: string; email: string };
  g2k_people: Array<{ name: string; email: string; duration_minutes: number }>;
  search_window_days: number;
}

export interface SendG2KsResult {
  /** New joiners whose scheduler run was started. */
  sent: Array<{ name: string; email: string }>;
  /** New joiners Starbase refused, with the reason — safe to send again. */
  failed: Array<{ name: string; email: string; error: string }>;
  /** New joiners whose call got no answer: the run may have started, so never resend blindly. */
  unconfirmed: Array<{ name: string; email: string; error: string }>;
}

export async function fetchNewJoiners(): Promise<NewJoinersResponse> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/onboarding/new-joiners`);
  return res.json() as Promise<NewJoinersResponse>;
}

/**
 * Groups entries by new joiner and starts the Starbase G2K scheduler once per joiner via the
 * proxy. A partial failure (the proxy's 502) resolves too, so the caller can keep only the
 * `failed` joiners queued and a retry never books anyone twice.
 */
export async function sendG2KsToScheduler(
  entries: G2KEntry[],
  searchWindowDays: number,
): Promise<SendG2KsResult> {
  const byJoiner = new Map<string, { joiner: NewJoiner; employees: G2KEntry[] }>();
  for (const entry of entries) {
    const key = entry.joiner.id;
    if (!byJoiner.has(key)) byJoiner.set(key, { joiner: entry.joiner, employees: [] });
    byJoiner.get(key)!.employees.push(entry);
  }

  const g2ks: G2KSchedulerPayload[] = [...byJoiner.values()].map(({ joiner, employees }) => ({
    new_joiner: { name: joiner.name, email: joiner.email },
    g2k_people: employees.map(e => ({
      name: e.employee.displayName,
      email: e.employee.email,
      duration_minutes: e.durationMinutes,
    })),
    search_window_days: searchWindowDays,
  }));

  const res = await authenticatedFetch(`${PROXY_URL}/api/onboarding/send-g2ks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ g2ks }),
    // A 502 carries the per-joiner result; it must reach the caller instead of throwing.
    suppressErrorStatuses: [502],
  });

  const body = await res.json().catch(() => ({})) as Partial<SendG2KsResult> & { error?: string };
  if (Array.isArray(body.sent) && Array.isArray(body.failed)) {
    return { sent: body.sent, failed: body.failed, unconfirmed: body.unconfirmed ?? [] };
  }
  throw new Error(body.error || `Failed to send G2Ks (${res.status})`);
}
